import { upperFirst } from "lodash";
import api from "@/utils/api";

const state = {
  user: {},
  team: {},
  project: {},
  projectRelease: {},
  subscription: {},
  playground: {},
  userEvents: { results: [] },
  sales: { results: [] },
  teamMemberships: [],
  teamProjects: [],
  teamSubscriptions: { stripe: [], paypal: [] },
  userPlaygrounds: [],
  teamPlaygrounds: [],
};

const mutations = {
  setUser(state, data) {
    state.user = data;
  },
  setTeam(state, data) {
    state.team = data;
  },
  setProject(state, data) {
    state.project = data;
  },
  setProjectRelease(state, data) {
    state.projectRelease = data;
  },
  setSubscription(state, data) {
    state.subscription = data;
  },
  setUserEvents(state, data) {
    state.userEvents = data;
  },
  setTeamMemberships(state, data) {
    state.teamMemberships = data;
  },
  setTeamProjects(state, data) {
    state.teamProjects = data;
  },
  setTeamSubscriptions(state, data) {
    state.teamSubscriptions = data;
  },
  setProjects(state, data) {
    state.projects = data;
  },
  setSales(state, data) {
    state.sales = data;
  },
  setPlayground(state, data) {
    state.playground = data;
  },
  setUserPlaygrounds(state, data) {
    state.userPlaygrounds = data;
  },
  setTeamPlaygrounds(state, data) {
    state.teamPlaygrounds = data;
  },
};

const action = async ({ commit, getters }, key, cb, shouldSave = true) => {
  if (getters.loading[key]) return;
  let value = null;
  try {
    commit("setLoading", key);
    value = await cb();
    if (shouldSave) commit(`set${upperFirst(key)}`, value);
    commit("unsetLoading", key);
    return value;
  } catch (e) {
    commit("unsetLoading", key);
    throw e;
  }
};

const getResourceListAction = (parent, parentId, child) => {
  return async () => {
    const {
      data: { results },
    } = await api.get(`/cs/${parent}/${parentId}/${child}`, {
      params: { page_size: 200 },
    });
    return results;
  };
};

const getResourceItemAction = (type, id) => {
  return async () => {
    const { data } = await api.get(`/cs/${type}/${id}`);
    return data;
  };
};

const getResourceBatchAction = (type, ids) => {
  return async () => {
    const { data } = await api.post(`/cs/${type}`, ids);
    return data;
  };
}

const putResourceItemAction = (type, id, payload) => {
  return async () => {
    const { data } = await api.put(`/cs/${type}/${id}`, payload);
    return data;
  };
};

const actions = {
  // user actions
  getUser(store, id) {
    return action(store, "user", getResourceItemAction("users", id));
  },
  getUsers(store, ids) {
    return action(store, "users", getResourceBatchAction("users", ids), false);
  },
  getTeamMemberships(store, { parent, parentId }) {
    return action(
      store,
      "teamMemberships",
      getResourceListAction(parent, parentId, "team_memberships")
    );
  },
  getUserEvents(store, { id, page_size = 20, page = 0, q = "" }) {
    return action(store, "userEvents", async () => {
      const params = { page_size, page, q };
      const { data } = await api.get(`/cs/users/${id}/events`, { params });
      return data;
    });
  },
  setUserPassword(store, { id, password }) {
    return action(store, "user", async () => {
      const { data } = await api.post(`/cs/users/${id}/set-password`, {
        password,
      });
      return data;
    });
  },
  updateUserPlan(store, { id }) {
    return action(store, "user", async () => {
      const { data } = await api.post(`/cs/users/${id}/upgrade`);
      return data;
    });
  },
  updateUser(store, { id, payload }) {
    return action(
      store,
      "user",
      putResourceItemAction("users", id, payload, false)
    );
  },
  updateTeamMembership(store, { id, payload }) {
    return action(
      store,
      "teamMembership",
      putResourceItemAction("team_memberships", id, payload),
      false
    );
  },

  // team actions
  getTeam(store, id) {
    return action(store, "team", async () => {
      const { data } = await api.get(`/cs/teams/${id}`);
      const { data: owner } = await api.get(`/cs/users/${data.owner}`);
      return { ...data, ownerDetails: owner };
    });
  },
  getSales(store, { page_size = 20, page = 0, start = null, end= null }) {
    return action(store, "sales", async () => {
      const params = { page_size, page, start, end };
      const { data } = await api.get(`/cs/sales`, { params });
      return data;
    });
  },
  getTeamProjects(store, teamId) {
    return action(
      store,
      "teamProjects",
      getResourceListAction("teams", teamId, "projects")
    );
  },
  getTeamSubscriptions(store, teamId) {
    return action(store, "teamSubscriptions", async () => {
      const stripeCallback = getResourceListAction(
        "teams",
        teamId,
        "subscriptions"
      );
      const paypalCallback = getResourceListAction(
        "teams",
        teamId,
        "paypal_subscriptions"
      );
      const stripe = await stripeCallback();
      const paypal = await paypalCallback();
      return { stripe, paypal };
    });
  },
  updateTeam(store, { id, payload }) {
    return action(store, "team", putResourceItemAction("teams", id, payload));
  },

  // project actions
  getProject(store, id) {
    return action(store, "project", getResourceItemAction("projects", id));
  },
  getProjectRelease(store, id) {
    return action(
      store,
      "projectRelease",
      getResourceItemAction("project_releases", id)
    );
  },
  updateProject(store, { id, payload }) {
    return action(
      store,
      "project",
      putResourceItemAction("projects", id, payload)
    );
  },

  // subscription actions
  getSubscription(store, { subscriptionType, id }) {
    const resource = `${
      subscriptionType === "paypal" ? "paypal_" : ""
    }subscriptions`;
    return action(store, "subscription", getResourceItemAction(resource, id));
  },
  updateSubscription(store, { subscriptionType, id, payload }) {
    const resource = `${
      subscriptionType === "paypal" ? "paypal_" : ""
    }subscriptions`;
    return action(
      store,
      "subscription",
      putResourceItemAction(resource, id, payload)
    );
  },
  deleteSubscription(store, { id, payload }) {
    return action(
      store,
      "subscription",
      putResourceItemAction("subscriptions", id, payload)
    );
  },

  // playground actions
  getPlayground(store, id) {
    return action(store, "playground", getResourceItemAction("playgrounds", id));
  },
  getUserPlaygrounds(store, userId) {
    return action(
      store,
      "userPlaygrounds",
      getResourceListAction("users", userId, "playgrounds")
    );
  },
  getTeamPlaygrounds(store, teamId) {
    return action(
      store,
      "teamPlaygrounds",
      getResourceListAction("teams", teamId, "playgrounds")
    );
  },
  updatePlayground(store, payload) {
    return action(
      store,
      "playground",
      putResourceItemAction("generation_sessions", payload.id, payload)
    );
  },
};

const getters = {
  user: (s) => s.user,
  team: (s) => s.team,
  project: (s) => s.project,
  projectRelease: (s) => s.projectRelease,
  subscription: (s) => s.subscription,
  playground: (s) => s.playground,
  userEvents: (s) => s.userEvents,
  teamMemberships: (s) => s.teamMemberships,
  teamProjects: (s) => s.teamProjects,
  teamSubscriptions: (s) => s.teamSubscriptions,
  userPlaygrounds: (s) => s.userPlaygrounds,
  teamPlaygrounds: (s) => s.teamPlaygrounds,
  projects: (s) => s.projects,
  sales: (s) => s.sales,
};

export default {
  state,
  mutations,
  actions,
  getters,
};
